import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getConfig, updateConfig, type ConfigAlojamiento } from "@/lib/config.functions";
import { getEmailStatus, sendTestEmail, runEmailsCicloAhora } from "@/lib/config.functions";
import { listServicios, upsertServicio, deleteServicio, type Servicio } from "@/lib/servicios.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { Loader2, Send, Mail } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/configuracion")({
  component: ConfigPage,
});

function ConfigPage() {
  const get = useServerFn(getConfig);
  const save = useServerFn(updateConfig);
  const [form, setForm] = useState<ConfigAlojamiento | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    get()
      .then((d) => setForm(d))
      .catch((e) => setErr(e?.message ?? "error"));
  }, [get]);

  function set<K extends keyof ConfigAlojamiento>(k: K, v: ConfigAlojamiento[K]) {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }

  async function handleSave() {
    if (!form) return;
    setSaving(true);
    try {
      await save({ data: {
        ...form,
        hora_checkin: form.hora_checkin.slice(0, 5),
        hora_checkout: form.hora_checkout.slice(0, 5),
      } });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (err) return <p className="text-sm text-destructive">{err}</p>;
  if (!form) return <p className="text-sm text-muted-foreground">Cargando configuración…</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl">Configuración del alojamiento</h2>
          <p className="text-sm text-muted-foreground">Datos fiscales, horarios y textos legales que aparecen en facturas y comunicaciones.</p>
        </div>
        <Button onClick={handleSave} disabled={saving}><Save className="mr-1 h-4 w-4" /> Guardar</Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Datos generales</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div><Label>Nombre comercial</Label><Input value={form.nombre_comercial} onChange={(e) => set("nombre_comercial", e.target.value)} /></div>
          <div><Label>Email de contacto</Label><Input type="email" value={form.email_contacto} onChange={(e) => set("email_contacto", e.target.value)} /></div>
          <div><Label>Teléfono de contacto</Label><Input value={form.telefono_contacto} onChange={(e) => set("telefono_contacto", e.target.value)} /></div>
          <div><Label>WhatsApp (solo dígitos, con prefijo país)</Label><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="34641696242" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Check-in</Label><Input type="time" value={form.hora_checkin.slice(0,5)} onChange={(e) => set("hora_checkin", e.target.value)} /></div>
            <div><Label>Check-out</Label><Input type="time" value={form.hora_checkout.slice(0,5)} onChange={(e) => set("hora_checkout", e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Textos de la web pública</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Título del hero (home)</Label><Input value={form.hero_titulo} onChange={(e) => set("hero_titulo", e.target.value)} /></div>
          <div><Label>Subtítulo del hero</Label><Textarea rows={2} value={form.hero_subtitulo} onChange={(e) => set("hero_subtitulo", e.target.value)} /></div>
          <div><Label>Intro del alojamiento (/alojamiento + home)</Label><Textarea rows={3} value={form.alojamiento_intro} onChange={(e) => set("alojamiento_intro", e.target.value)} /></div>
          <div><Label>Intro del entorno (/entorno)</Label><Textarea rows={3} value={form.entorno_intro} onChange={(e) => set("entorno_intro", e.target.value)} /></div>
          <div><Label>Intro de contacto (/contacto)</Label><Textarea rows={3} value={form.contacto_intro} onChange={(e) => set("contacto_intro", e.target.value)} /></div>
          <div><Label>Nota de tarifas (/alojamiento)</Label><Textarea rows={3} value={form.tarifas_nota} onChange={(e) => set("tarifas_nota", e.target.value)} /></div>
        </CardContent>
      </Card>

      <ServiciosCard />

      <Card>
        <CardHeader><CardTitle className="text-base">Email transaccional (Resend)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <EmailStatusBanner />
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Email remitente</Label>
              <Input value={form.email_remitente} onChange={(e) => set("email_remitente", e.target.value)} placeholder="reservas@elpajardebenito.es" />
              <p className="mt-1 text-xs text-muted-foreground">Debe pertenecer al dominio verificado en Resend.</p>
            </div>
            <div>
              <Label>Nombre del remitente</Label>
              <Input value={form.email_remitente_nombre} onChange={(e) => set("email_remitente_nombre", e.target.value)} placeholder="El Pajar de Benito" />
            </div>
            <div className="md:col-span-2">
              <Label>Reply-to (opcional)</Label>
              <Input value={form.email_reply_to} onChange={(e) => set("email_reply_to", e.target.value)} placeholder="info@elpajardebenito.es" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">La clave API de Resend se guarda como secreto del servidor — nunca se expone en la web. Para rotarla, actualiza el secreto <code>RESEND_API_KEY</code>.</p>
          <TestEmailRow />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Emails automáticos del ciclo de reserva</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Cada día se envía automáticamente:
          </p>
          <ul className="ml-5 list-disc space-y-1 text-sm">
            <li><strong>Pre-llegada</strong>: 3 días antes del check-in, con recordatorio y datos útiles.</li>
            <li><strong>Post-estancia</strong>: al día siguiente del check-out, agradeciendo y pidiendo reseña.</li>
          </ul>
          <p className="text-xs text-muted-foreground">
            Se guarda la marca de envío en cada reserva para no duplicar. Si quieres forzar el ciclo ahora mismo (útil para probar), usa el botón.
          </p>
          <RunCicloRow />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Datos fiscales</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div><Label>Nombre / Razón social</Label><Input value={form.nombre_fiscal} onChange={(e) => set("nombre_fiscal", e.target.value)} /></div>
          <div><Label>NIF / CIF</Label><Input value={form.nif} onChange={(e) => set("nif", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} /></div>
          <div><Label>Código postal</Label><Input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} /></div>
          <div><Label>Ciudad</Label><Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} /></div>
          <div><Label>País</Label><Input value={form.pais} onChange={(e) => set("pais", e.target.value)} /></div>
          <div><Label>IVA aplicable (%)</Label><Input type="number" step="0.01" value={form.iva_porcentaje} onChange={(e) => set("iva_porcentaje", Number(e.target.value))} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Datos registrales (aparecen en aviso legal)</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Registro Mercantil</Label>
            <Input value={form.registro_mercantil ?? ""} onChange={(e) => set("registro_mercantil", e.target.value)} placeholder="p.ej. Registro Mercantil de Madrid, Tomo 12345, Folio 67, Hoja M-89012" />
          </div>
          <div>
            <Label>Nº de registro turístico (JCCM)</Label>
            <Input value={form.numero_registro_turistico ?? ""} onChange={(e) => set("numero_registro_turistico", e.target.value)} placeholder="p.ej. CR-GU-000000" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Facturación</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div><Label>Prefijo de factura</Label><Input value={form.prefijo_factura} onChange={(e) => set("prefijo_factura", e.target.value)} /></div>
          <div><Label>Próximo nº de factura</Label><Input type="number" min={1} value={form.proximo_numero_factura} onChange={(e) => set("proximo_numero_factura", Number(e.target.value))} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Textos legales</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Política de cancelación</Label><Textarea rows={4} value={form.politica_cancelacion} onChange={(e) => set("politica_cancelacion", e.target.value)} /></div>
          <div><Label>Condiciones generales</Label><Textarea rows={6} value={form.condiciones_generales} onChange={(e) => set("condiciones_generales", e.target.value)} /></div>
          <div><Label>Aviso legal</Label><Textarea rows={6} value={form.aviso_legal} onChange={(e) => set("aviso_legal", e.target.value)} /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}><Save className="mr-1 h-4 w-4" /> Guardar cambios</Button>
      </div>
    </div>
  );
}

function ServiciosCard() {
  const list = useServerFn(listServicios);
  const upsert = useServerFn(upsertServicio);
  const remove = useServerFn(deleteServicio);
  const [items, setItems] = useState<Servicio[]>([]);
  const [nuevo, setNuevo] = useState("");

  async function reload() {
    try { setItems(await list()); } catch (e) { toast.error((e as Error).message); }
  }
  useEffect(() => { void reload(); }, []);

  async function add() {
    const nombre = nuevo.trim();
    if (!nombre) return;
    try {
      await upsert({ data: { nombre, icono: "", orden: items.length, activo: true } });
      setNuevo("");
      await reload();
    } catch (e) { toast.error((e as Error).message); }
  }
  async function patch(s: Servicio, partial: Partial<Servicio>) {
    try {
      await upsert({ data: { id: s.id, nombre: partial.nombre ?? s.nombre, icono: s.icono, orden: partial.orden ?? s.orden, activo: partial.activo ?? s.activo } });
      await reload();
    } catch (e) { toast.error((e as Error).message); }
  }
  async function del(id: string) {
    if (!confirm("¿Eliminar este servicio?")) return;
    try { await remove({ data: { id } }); await reload(); } catch (e) { toast.error((e as Error).message); }
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Servicios incluidos (web pública)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input placeholder="Nuevo servicio (p.ej. Wi-Fi)" value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }} />
          <Button type="button" onClick={() => void add()}><Plus className="mr-1 h-4 w-4" /> Añadir</Button>
        </div>
        <ul className="divide-y rounded border">
          {items.map((s) => (
            <li key={s.id} className="flex items-center gap-2 p-2">
              <Input className="flex-1" value={s.nombre} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, nombre: e.target.value } : x))} onBlur={(e) => { if (e.target.value !== s.nombre) void patch(s, { nombre: e.target.value }); }} />
              <Input type="number" className="w-20" value={s.orden} onChange={(e) => setItems((xs) => xs.map((x) => x.id === s.id ? { ...x, orden: Number(e.target.value) } : x))} onBlur={(e) => { const n = Number(e.target.value); if (n !== s.orden) void patch(s, { orden: n }); }} />
              <div className="flex items-center gap-2 px-2"><Switch checked={s.activo} onCheckedChange={(v) => void patch(s, { activo: v })} /><span className="text-xs text-muted-foreground">Activo</span></div>
              <Button type="button" variant="ghost" size="icon" onClick={() => void del(s.id)}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
          {items.length === 0 && <li className="p-3 text-sm text-muted-foreground">Sin servicios. Añade el primero.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}
function EmailStatusBanner() {
  const get = useServerFn(getEmailStatus);
  const [s, setS] = useState<{ configurada: boolean } | null>(null);
  useEffect(() => { get().then(setS).catch(() => setS({ configurada: false })); }, [get]);
  if (!s) return <p className="text-xs text-muted-foreground">Comprobando estado…</p>;
  return (
    <div className={`rounded border p-3 text-sm ${s.configurada ? "border-green-200 bg-green-50 text-green-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
      {s.configurada
        ? "✓ Clave API de Resend configurada en el servidor."
        : "⚠ Clave API de Resend no configurada. Los envíos de email fallarán hasta que se añada el secreto RESEND_API_KEY."}
    </div>
  );
}

function TestEmailRow() {
  const send = useServerFn(sendTestEmail);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!to.trim()) { toast.error("Indica un email de destino"); return; }
    setBusy(true);
    try {
      await send({ data: { to: to.trim() } });
      toast.success("Email de prueba enviado");
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-end gap-2 border-t pt-3">
      <div className="flex-1 min-w-[200px]">
        <Label>Enviar email de prueba a</Label>
        <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="tu@correo.com" />
      </div>
      <Button type="button" onClick={() => void go()} disabled={busy}>
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
        Enviar prueba
      </Button>
    </div>
  );
}

function RunCicloRow() {
  const run = useServerFn(runEmailsCicloAhora);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<string | null>(null);
  async function go() {
    setBusy(true);
    try {
      const r = await run();
      const msg = `Pre-llegada: ${r.prellegada} · Post-estancia: ${r.postestancia}${r.errores.length ? ` · Errores: ${r.errores.length}` : ""}`;
      setLast(msg);
      if (r.errores.length) toast.error(`Enviados con errores. ${msg}`);
      else toast.success(`Ciclo enviado. ${msg}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 border-t pt-3">
      <Button type="button" onClick={() => void go()} disabled={busy}>
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
        Ejecutar ciclo ahora
      </Button>
      {last && <span className="text-xs text-muted-foreground">{last}</span>}
    </div>
  );
}
